import React from 'react';
import { FC } from 'react';
import { Router } from 'react-router-dom';
import sharedHistory from 'src/utils/history';
import { create } from 'jss';
import rtl from 'jss-rtl';
import MomentUtils from '@date-io/moment';
import ToastProvider from 'src/components/ToastProvider';
import { jssPreset, StylesProvider, ThemeProvider } from '@material-ui/core';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import GlobalStyles from 'src/components/GlobalStyles';
import ScrollReset from 'src/components/ScrollReset';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { AuthProvider } from 'src/contexts/Auth0Context';
import useSettings from 'src/hooks/useSettings';
import { createTheme } from 'src/theme';
import { prodRoutes, renderRoutes } from 'src/routes';
import { ApolloCustomProvider } from 'src/lib/apollo';
import { BrandProvider } from './contexts/BrandContext';

const jss = create({ plugins: [...jssPreset().plugins, rtl()] });
// Shared with Auth0Context: it navigates after login (back to the page you
// were on); a second history instance meant the Router never saw that push.
const history = sharedHistory;

const App: FC = () => {
  const { settings } = useSettings();

  const theme = createTheme({
    direction: settings.direction,
    responsiveFontSizes: settings.responsiveFontSizes,
    theme: settings.theme
  });

  return (
    <ThemeProvider theme={theme}>
      <StylesProvider jss={jss}>
        <MuiPickersUtilsProvider utils={MomentUtils}>
          <ToastProvider>
            <Router history={history}>
              <AuthProvider>
                <BrandProvider>
                  <ApolloCustomProvider>
                    <GlobalStyles />
                    <ScrollReset />
                    {/* Backstop for crashes outside the dashboard layout
                        (login, 404, providers); pages use the one in the layout. */}
                    <ErrorBoundary fullPage>{renderRoutes(prodRoutes)}</ErrorBoundary>
                  </ApolloCustomProvider>
                </BrandProvider>
              </AuthProvider>
            </Router>
          </ToastProvider>
        </MuiPickersUtilsProvider>
      </StylesProvider>
    </ThemeProvider>
  );
};

export default App;
